// assets/birth-place.js
// Birth place autocomplete via Photon (photon.komoot.io) — a free, no-key, OpenStreetMap-based
// geocoder built for autocomplete (sends CORS headers for browser use). No vendored library.
//
// On selection it fills the visible "Birth Place" property and captures exact lat/lng as hidden
// line-item properties (_Birth Lat / _Birth Lng) so the chart engine receives precise coordinates
// instead of geocoding a free-typed string. Timezone is derived server-side from the coordinates.
//
// Note: Photon is free for fair use and data is © OpenStreetMap contributors. For high volume,
// move to a self-hosted Photon or a paid provider (Google/Mapbox) — the field wiring stays the same.

const ENDPOINT = 'https://photon.komoot.io/api/';
const MIN_CHARS = 3;
const DEBOUNCE_MS = 300;
// the place types a birth certificate names, shown untagged; everything else carries its type
const PLAIN_PLACE_TYPES = new Set(['city', 'town', 'village', 'hamlet']);
// populated places (sorted above counties/regions/districts in the suggestion list)
const POPULATED_PLACE_TYPES = new Set(['city', 'town', 'village', 'hamlet', 'suburb', 'quarter', 'neighbourhood', 'borough', 'locality']);
// fetch DEEP, show few: for a bare "Warren" Photon's top 10 is nine counties and one city, with
// the other real Warrens (incl. Trumbull County, Ohio's) ranked 11-30. Populated-first sorting
// over a deep fetch puts the towns in the visible rows and sinks the counties.
const FETCH_LIMIT = 30;
const MAX_SUGGESTIONS = 8;

class BirthPlaceField extends HTMLElement {
  connectedCallback() {
    /** @type {HTMLInputElement | null} */
    this.input = this.querySelector('[data-place-input]');
    /** @type {HTMLElement | null} */
    this.suggestions = this.querySelector('[data-place-suggestions]');
    /** @type {HTMLInputElement | null} */
    this.latEl = this.querySelector('[data-place-lat]');
    /** @type {HTMLInputElement | null} */
    this.lngEl = this.querySelector('[data-place-lng]');
    if (!this.input || this.dataset.ready) return;
    this.dataset.ready = 'true';
    /** @type {number} */
    this.timer = 0;

    this.input.addEventListener('input', () => this.handleInput());
    this.input.addEventListener('keydown', (event) => {
      if (/** @type {KeyboardEvent} */ (event).key === 'Escape') this.close();
    });
    this.input.addEventListener('blur', () => setTimeout(() => this.close(), 150));
    document.addEventListener('click', (event) => {
      if (!this.contains(/** @type {Node | null} */ (event.target))) this.close();
    });
  }

  handleInput() {
    if (!this.input) return;
    // Typing invalidates any prior coordinate selection.
    if (this.latEl) this.latEl.value = '';
    if (this.lngEl) this.lngEl.value = '';
    this.notifyChange();

    const query = this.input.value.trim();
    clearTimeout(this.timer);
    if (query.length < MIN_CHARS) {
      this.close();
      return;
    }
    this.timer = setTimeout(() => this.search(query), DEBOUNCE_MS);
  }

  /** @param {string} query */
  async search(query) {
    // osm_tag=place biases results toward populated places (city/town/village) over POIs/streets.
    const url =
      `${ENDPOINT}?q=${encodeURIComponent(query)}&limit=${FETCH_LIMIT}&lang=en&osm_tag=place`;
    try {
      const res = await fetch(url, { headers: { Accept: 'application/json' } });
      if (!res.ok) return;
      const data = await res.json();
      if (this.input && this.input.value.trim().length >= MIN_CHARS) {
        this.render(Array.isArray(data.features) ? data.features : []);
      }
    } catch (err) {
      // Offline or rate-limited: fail quietly, leave the typed value as a fallback.
    }
  }

  /** @param {any} feature */
  label(feature, { county = false } = {}) {
    const p = feature.properties || {};
    const name = p.name || p.city || '';
    const t = String(p.osm_value || '').replace(/_/g, ' ');
    // a county/region/district named "Warren" is indistinguishable from Warren the city unless
    // its type is on the label, so every non-birth-certificate place type is tagged
    const typed = name && t && !PLAIN_PLACE_TYPES.has(t) ? `${name} (${t})` : name;
    const parts = [typed, county ? p.county : '', p.state, p.country].filter(Boolean);
    return parts.length ? parts.join(', ') : name;
  }

  /** @param {any[]} features */
  render(features) {
    if (!this.suggestions || !this.input) return;
    this.suggestions.innerHTML = '';
    // A birth chart's houses come from the EXACT birthplace, and "Warren" alone matches counties
    // in nine states plus several towns. Order #1003 proved the failure: Photon ranked Warren
    // COUNTY, Ohio first, the label ("Warren, Ohio, United States") looked like the city, and the
    // real city carried the SAME label so the dedupe dropped it — the right answer was unpickable
    // and the book got the wrong rising sign. Three rules make every row honest:
    //   1. admin areas are always type-tagged (see label()): "Warren (county), Ohio";
    //   2. populated places sort ABOVE admin areas (a birthplace is a populated place);
    //   3. rows that still share a label gain their county, and only then does dedupe run, so two
    //      different places can never fold into one suggestion.
    /** @param {any} f */
    const isPopulated = (f) => POPULATED_PLACE_TYPES.has(String((f.properties || {}).osm_value || '').replace(/_/g, ' '));
    const ordered = [...features.filter(isPopulated), ...features.filter((f) => !isPopulated(f))];
    /** @param {{feature: any, text: string}[]} list */
    const dupCounts = (list) => {
      const m = new Map();
      for (const it of list) m.set(it.text, (m.get(it.text) || 0) + 1);
      return m;
    };
    let items = ordered.map((feature) => ({ feature, text: this.label(feature) }));
    const counts = dupCounts(items);
    items = items.map((it) => (counts.get(it.text) > 1 ? { feature: it.feature, text: this.label(it.feature, { county: true }) } : it));
    const seen = new Set();
    items = items.filter((it) => {
      if (!it.text || seen.has(it.text)) return false;
      seen.add(it.text);
      return true;
    }).slice(0, MAX_SUGGESTIONS);
    if (items.length === 0) {
      this.close();
      return;
    }
    for (const { feature, text } of items) {
      const li = document.createElement('li');
      li.className = 'birth-place__suggestion';
      li.setAttribute('role', 'option');
      li.textContent = text;
      // mousedown (not click) so it fires before the input's blur.
      li.addEventListener('mousedown', (event) => {
        event.preventDefault();
        this.select(feature, text);
      });
      this.suggestions.appendChild(li);
    }
    this.suggestions.hidden = false;
    this.input.setAttribute('aria-expanded', 'true');
  }

  /** @param {any} feature @param {string} [text] */
  select(feature, text) {
    if (!this.input) return;
    // store the DISAMBIGUATED label the customer clicked (county and all), so what they picked is
    // what the order carries and what the book's records show
    this.input.value = text || this.label(feature);
    const coords = (feature.geometry && feature.geometry.coordinates) || [];
    if (this.lngEl) this.lngEl.value = coords[0] != null ? String(coords[0]) : '';
    if (this.latEl) this.latEl.value = coords[1] != null ? String(coords[1]) : '';
    this.notifyChange();
    this.close();
  }

  // Hidden lat/lng set via JS don't emit input events; announce the change so
  // listeners (e.g. the chart preview's readiness watcher) can react.
  notifyChange() {
    this.dispatchEvent(new Event('input', { bubbles: true }));
  }

  close() {
    if (this.suggestions) {
      this.suggestions.hidden = true;
      this.suggestions.innerHTML = '';
    }
    if (this.input) this.input.setAttribute('aria-expanded', 'false');
  }
}

if (!customElements.get('birth-place-field')) {
  customElements.define('birth-place-field', BirthPlaceField);
}
